import * as Sentry from '@sentry/vue';

export type SentryClientDebugLogLevel = 'log' | 'info' | 'warn';

/**
 * Flattens detail objects into Sentry log attributes.
 * - Primitives stay as filterable attributes
 * - Nested objects/arrays become JSON strings under the same key
 * - Full payload is also attached as `details_json` for easy inspection
 */
export function toSentryLogAttributes(
	details: Record<string, unknown> | undefined,
): Record<string, string | number | boolean> | undefined {
	if (!details) {
		return undefined;
	}

	const attributes: Record<string, string | number | boolean> = {};

	for (const [key, value] of Object.entries(details)) {
		if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
			attributes[key] = value;
			continue;
		}

		if (value === null) {
			attributes[key] = 'null';
			continue;
		}

		try {
			attributes[key] = JSON.stringify(value);
		} catch {
			attributes[key] = String(value);
		}
	}

	try {
		attributes.details_json = JSON.stringify(details);
	} catch {
		// Ignore — individual keys above are still present.
	}

	return attributes;
}

/**
 * Sink for {@link setClientDebugLogSink} from `@apexx/auth`.
 * Sends already-sanitized auth/OAuth/logout lines to Sentry Logs (console is handled by auth).
 */
export function createSentryClientDebugSink(): (
	level: SentryClientDebugLogLevel,
	message: string,
	details?: Record<string, unknown>,
) => void {
	return (level, message, details) => {
		if (!Sentry.getClient()) {
			return;
		}

		try {
			const attributes = toSentryLogAttributes(details);

			if (level === 'warn') {
				Sentry.logger.warn(message, attributes);
				return;
			}

			Sentry.logger.info(message, attributes);
		} catch {
			// Share/auth flows must not break if Sentry Logs rejects a payload.
		}
	};
}
